import { type AskPunttReview } from "@mg/schemas/src/christo/askPunttReview";
import { CaretDown } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type ReviewVersionOption = {
  reviewId: string;
  title: string;
  /** The version christo recorded for the review (warhol spec kevin.johnson/0007); absent when unlinked. */
  versionNumber?: number;
};

export function VersionPicker({
  review,
  options,
  onSelect,
  testIdPrefix = "ask-v2-review-version",
  label,
  mark,
}: {
  review: AskPunttReview;
  options: readonly ReviewVersionOption[];
  onSelect: (reviewId: string) => void;
  /**
   * Prefix for the trigger's and every option's testid. A comparison mounts a
   * second picker beside the first, and the two must not share one id.
   */
  testIdPrefix?: string;
  /** Accessible name; defaults to "Review version". */
  label?: string;
  /**
   * A short mark before the title ("V2"). Two pickers side by side both
   * truncate "Review: <file name>" to the same words; this tells them apart.
   */
  mark?: string;
}) {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const disabled = options.length <= 1;
  const name = label ?? t("askV2.review.versionPickerLabel");
  const current = options.find((option) => option.reviewId === review.reviewId);
  // A comparison names its side itself; otherwise the open review's recorded version.
  const shownMark =
    mark ??
    (current?.versionNumber != null ? `V${current.versionNumber}` : undefined);

  // why: this menu is a hand-rolled popup, not a Radix one, so nothing ever
  // dismissed it. Opening it and then reading on -- clicking the artwork, a
  // finding, the close control -- left it hanging over the header at z-20
  // until the trigger happened to be pressed a second time, and Escape did
  // nothing at all. Capture phase because the artwork pane takes pointerdown
  // for pan and pin placement; a target that stops propagation must not be
  // able to keep the menu open.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // why: claims the key, so the comparison's Esc shortcut closes only
      // this menu and not the whole comparison with it.
      event.preventDefault();
      setOpen(false);
      // A reader who tabbed into the list would otherwise be dropped on
      // <body> when the option they were on unmounts.
      triggerRef.current?.focus();
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [open]);

  return (
    // why: min-w-0 lets the picker give up width in a crowded row (the
    // compare bar) instead of pushing Exit compare off the edge.
    <div className="relative min-w-0" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        aria-disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${name}: ${review.title}`}
        title={review.title}
        data-testid={`${testIdPrefix}-picker`}
        className="flex max-w-[min(220px,100%)] items-center gap-1 rounded-md border border-puntt-neutral-gray-5 bg-base-white px-2.5 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-12 disabled:cursor-default disabled:opacity-70"
        onClick={() => !disabled && setOpen((current) => !current)}
      >
        {shownMark ? (
          <span
            className="shrink-0 rounded bg-puntt-neutral-gray-3 px-1 text-[11px] font-semibold"
            data-testid={`${testIdPrefix}-mark`}
          >
            {shownMark}
          </span>
        ) : null}
        <span className="min-w-0 truncate">{review.title}</span>
        <CaretDown size={14} aria-hidden="true" />
      </button>
      {open && options.length > 1 ? (
        <ul
          className="absolute left-0 top-full z-20 mt-1 min-w-[220px] rounded-md border border-puntt-neutral-gray-5 bg-base-white py-1 shadow-lg"
          role="listbox"
          // why: the popup that opens is a second thing to name, not the same
          // thing as the trigger -- unnamed, a reader who opens it is told only
          // "list box" and has to infer what it is picking between.
          aria-label={name}
        >
          {options.map((option) => (
            // why: a `listbox` may own only `option` (or `group`). Left as a
            // plain `<li>` each wrapper is a `listitem`, so this menu reached a
            // reader as a two-item list with a button inside each -- no "option
            // 1 of 2", and the `aria-selected` that marks the version currently
            // open sat on an element the listbox does not own. That is the same
            // axe aria-required-children failure the pane tabs were fixed
            // for. `role="none"` drops the wrapper out of the accessibility
            // tree so the listbox owns the option buttons directly, and leaves
            // focus, clicks and the testid on the button where they already are.
            <li key={option.reviewId} role="none">
              <button
                type="button"
                role="option"
                aria-selected={option.reviewId === review.reviewId}
                className="block w-full px-3 py-2 text-left text-[13px] hover:bg-puntt-neutral-gray-2"
                data-testid={`${testIdPrefix}-option-${option.reviewId}`}
                onClick={() => {
                  setOpen(false);
                  onSelect(option.reviewId);
                }}
              >
                {option.versionNumber != null ? (
                  <span
                    className="mr-2 inline-block rounded bg-puntt-neutral-gray-3 px-1 text-[11px] font-semibold"
                    data-testid={`${testIdPrefix}-option-${option.reviewId}-version`}
                  >
                    V{option.versionNumber}
                  </span>
                ) : null}
                {/* why the space: without it a reader hears "V1Pack review". */}
                {option.versionNumber != null ? " " : null}
                {option.title}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
